import { useState, useEffect, useContext, useCallback } from 'react';
import Select from 'react-select'; 
import { AppContext } from '../App';
import authService from '../services/authService';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/AlertDialog"; 
import { CustomerEditDialog } from './ui/CustomerEditDialog';
import config from '../config';
import { Eye, EyeOff, Copy, Edit2, Trash2, ToggleLeft, ToggleRight, PlusCircle } from 'lucide-react'; 
import { NewCustomerModal } from './apiKeyManagement/NewCustomerModal';
import { ApiKeyList } from './apiKeyManagement/ApiKeyList';
import { GenerateKeyForm } from './apiKeyManagement/GenerateKeyForm';
import CustomerSelfServicePanel from './CustomerSelfServicePanel';
import { trackEvent } from '../analytics';

export default function ApiKeyManagementScreen() {
  const { selectedTeam } = useContext(AppContext);
  const [apiKeys, setApiKeys] = useState([]); 
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ displayName: '', email: '' });
  const [editingCustomer, setEditingCustomer] = useState(null); 
  
  const [keyForm, setKeyForm] = useState({ nickname: '', ttl: 24, usageLimit: '' });
  const [teamProducts, setTeamProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  let errorTimer;
  let successTimer;

  useEffect(() => {
    if (error) {
      clearTimeout(errorTimer);
      errorTimer = setTimeout(() => setError(''), 5000); 
    }
    return () => clearTimeout(errorTimer);
  }, [error]);

  useEffect(() => {
    if (success) {
      clearTimeout(successTimer);
      successTimer = setTimeout(() => setSuccess(''), 3000); 
    }
    return () => clearTimeout(successTimer);
  }, [success]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/products/GetOwnedProducts`, {
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch owned products');
      const data = await response.json();
      console.log('Fetched owned products:', data);
      setTeamProducts(data.products || []); 
    } catch (err) {
      setError('Failed to load your products');
      setTeamProducts([]); 
    }
  }, []);

  const fetchPlans = useCallback(async (productId) => {
    setLoadingPlans(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/plans?productId=${productId}`, {
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const data = await response.json();
      setPlans(data.plans || []); 
    } catch (err) {
      setError('Failed to load plans for the selected product');
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const fetchCustomerKeys = useCallback(async (customerGuid) => {
    if (!customerGuid) {
      setApiKeys([]); 
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/customers/${customerGuid}/keys`, {
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch API keys');
      const data = await response.json();
      setApiKeys(data || []); 
    } catch (error) {
      setError('Failed to fetch API keys for the selected customer');
      setApiKeys([]); 
    }
  }, []); 

  const handleUpdateCustomer = async (customerGuid, updates) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/customers/${customerGuid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...updates })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update customer');
      }
      setSuccess('Customer updated successfully');
      fetchCustomers(); 
      setEditingCustomer(null); 
    } catch (error) {
      setError(`Failed to update customer: ${error.message}`);
    }
  };
  
  const handleDeleteCustomer = async (customerGuid) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/customers/${customerGuid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete customer');
      }
      setSuccess('Customer deleted successfully');
      if (selectedCustomer?.CustomerGUID === customerGuid) {
         setSelectedCustomer(null); 
      }
      setEditingCustomer(null); 
      fetchCustomers(); 
    } catch (error) {
      setError(`Failed to delete customer: ${error.message}`);
    }
  };
  
  const handleCreateCustomer = async () => {
    if (!selectedTeam?.teamId) {
      setError('No team selected to associate the customer with.');
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          displayName: customerForm.displayName.trim(),
          email: customerForm.email.trim(),
          teamId: selectedTeam.teamId
        })
      });
      
       if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create customer');
      }
      
      trackEvent('create_customer');
      setSuccess('Customer created successfully');
      setIsAddingCustomer(false);
      setCustomerForm({ displayName: '', email: '' });
      fetchCustomers();
    } catch (error) {
      setError(`Failed to create customer: ${error.message}`);
    }
  };

  const handleCustomerSelect = (selectedOption) => {
    if (selectedOption) {
      const customer = customers.find(c => c.CustomerGUID === selectedOption.value);
      setSelectedCustomer(customer);
      fetchCustomerKeys(customer.CustomerGUID);
    } else {
      setSelectedCustomer(null);
    }
  };
  
  const handleGenerateKey = async () => {
    if (isGenerating) return;
    try {
      if (!selectedCustomer || !selectedProduct || !selectedPlan || !keyForm.nickname) {
        setError('Please select a customer, product, plan, and provide a nickname.');
        return;
      }

      setIsGenerating(true);

      const payload = {
        productId: selectedProduct.id,
        planId: selectedPlan.id,
        nickname: keyForm.nickname,
        customerTeamId: selectedCustomer.CustomerGUID,
      };

      // Optional lifetime cap. Only send when a positive integer was entered;
      // blank/invalid means unlimited.
      const parsedLimit = parseInt(keyForm.usageLimit, 10);
      if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
        payload.usageLimit = parsedLimit;
      }

      console.log('Generating key with payload:', payload);

      const response = await fetch(`${config.apiBaseUrl}/api/keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate API key');
      }

      const result = await response.json();
      trackEvent('issue_api_key');
      setSuccess(`API Key generated successfully! Key: ${result.apiKey.id}`);
      fetchCustomerKeys(selectedCustomer.CustomerGUID);
      setKeyForm({ nickname: '', ttl: 24, usageLimit: '' });
      setSelectedProduct(null);
      setSelectedPlan(null);
      setPlans([]);

    } catch (error) {
      console.error('Error in handleGenerateKey:', error);
      setError(`Failed to generate API key: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/keys/${keyId}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authService.getToken()}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete key');
      }
      setSuccess('API key deleted successfully');
      if (selectedCustomer) {
         fetchCustomerKeys(selectedCustomer.CustomerGUID); 
      }
    } catch (error) {
      setError(`Failed to delete API key: ${error.message}`);
    }
  };

  const handleToggleKey = async (keyId, currentState) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/keys/${keyId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isEnabled: !currentState })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle key');
      }
      setSuccess(`API key ${currentState ? 'disabled' : 'enabled'} successfully`);
      if (selectedCustomer) {
         fetchCustomerKeys(selectedCustomer.CustomerGUID); 
      }
    } catch (error) {
      setError(`Failed to toggle API key: ${error.message}`);
    }
  };

  const handleUpdateNickname = async (keyId, newNickname) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/keys/${keyId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nickname: newNickname })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update nickname');
      }
      setSuccess('Nickname updated successfully');
      if (selectedCustomer) {
         fetchCustomerKeys(selectedCustomer.CustomerGUID); 
      }
    } catch (error) {
      setError(`Failed to update nickname: ${error.message}`);
    }
  };

  const fetchCustomers = useCallback(async () => {
    if (!selectedTeam?.teamId) {
      console.log("No team selected, skipping customer fetch");
      setCustomers([]); 
      return;
    }
    console.log(`Fetching customers for team: ${selectedTeam.teamId}`);
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/customers?vendorTeamId=${selectedTeam.teamId}`, {
        headers: { "Authorization": `Bearer ${authService.getToken()}`, }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch customers:", response.status, errorText);
        throw new Error(`Failed to fetch customers (status: ${response.status})`);
      }
      const data = await response.json();
      console.log("Fetched customers data:", data);
      setCustomers(data || []); 
    } catch (error) {
      console.error("Error in fetchCustomers:", error);
      setError('Failed to load customers');
      setCustomers([]); 
    }
  }, [selectedTeam]);

  useEffect(() => {
    console.log("Selected team changed, fetching products and customers:", selectedTeam);
    if (selectedTeam?.teamId) {
      fetchProducts(); 
      fetchCustomers(); 
      setSelectedCustomer(null); 
      setApiKeys([]); 
      setSelectedProduct(null);
      setSelectedPlan(null);
      setPlans([]);
    } else {
      setTeamProducts([]);
      setCustomers([]);
      setSelectedCustomer(null);
      setApiKeys([]); 
      setSelectedProduct(null);
      setSelectedPlan(null);
      setPlans([]);
    }
  }, [selectedTeam, fetchProducts, fetchCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerKeys(selectedCustomer.CustomerGUID);
    } else {
      setApiKeys([]); 
    }
  }, [selectedCustomer, fetchCustomerKeys]);

  const customerOptions = customers.map(customer => ({
    value: customer.CustomerGUID,
    label: customer.DisplayName
  }));

  const selectedCustomerOption = customerOptions.find(
    option => option.value === selectedCustomer?.CustomerGUID
  );

  return (
    <div className="text-slate-100">
      <div className="max-w-5xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100">API Key Management</h1>
            <p className="text-sm text-slate-500 mt-1">
              Issue and manage API keys for your customers.{' '}
              <a href="/docs/cloud-integration.html" className="text-indigo-400 hover:text-indigo-300">
                Integration guide →
              </a>
            </p>
          </div>
          <button
            onClick={() => setIsAddingCustomer(true)}
            disabled={!selectedTeam}
            className="flex items-center gap-2 bg-slate-800 border border-slate-700 hover:border-slate-600 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusCircle size={16} /> New Customer
          </button>
        </header>

        {error && (
          <div className="mb-6 p-3 bg-red-950/50 border border-red-800/50 text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-3 bg-emerald-950/50 border border-emerald-800/50 text-emerald-300 rounded-lg text-sm">
            {success}
          </div>
        )}

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-slate-100">Select Customer</h2>
          <div className="flex gap-2 items-center">
            <Select
              options={customerOptions}
              value={selectedCustomerOption}
              onChange={handleCustomerSelect}
              placeholder="Choose or search for a customer..."
              isClearable
              isDisabled={!selectedTeam || customers.length === 0}
              className="react-select-container flex-1"
              classNamePrefix="react-select"
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: '#020617',
                  borderColor: state.isFocused ? '#334155' : '#1e293b',
                  color: '#e2e8f0',
                  minHeight: '42px',
                  boxShadow: 'none',
                  '&:hover': { borderColor: '#334155' }
                }),
                menu: (baseStyles) => ({ ...baseStyles, backgroundColor: '#020617', border: '1px solid #1e293b', color: '#e2e8f0' }),
                option: (baseStyles, state) => ({ ...baseStyles, backgroundColor: state.isFocused ? '#1e293b' : state.isSelected ? '#4f46e5' : 'transparent', '&:active': { backgroundColor: '#1e293b' }, color: '#e2e8f0' }),
                singleValue: (baseStyles) => ({ ...baseStyles, color: '#e2e8f0' }),
                input: (baseStyles) => ({ ...baseStyles, color: '#e2e8f0' }),
                placeholder: (baseStyles) => ({ ...baseStyles, color: '#64748b' }),
                indicatorSeparator: () => ({ display: 'none' }),
                dropdownIndicator: (base) => ({ ...base, color: '#64748b', '&:hover': { color: '#94a3b8' } }),
                clearIndicator: (base) => ({ ...base, color: '#64748b', '&:hover': { color: '#ef4444' } }),
              }}
            />
            <button
              onClick={() => selectedCustomer && setEditingCustomer(selectedCustomer)}
              className="p-3 h-[42px] hover:bg-slate-700 rounded-lg border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              disabled={!selectedCustomer}
              title="Edit customer"
            >
              <Edit2 size={18} />
            </button>
          </div>
        </div>

        <NewCustomerModal
          isOpen={isAddingCustomer}
          onClose={() => setIsAddingCustomer(false)}
          customerForm={customerForm}
          setCustomerForm={setCustomerForm}
          handleCreateCustomer={handleCreateCustomer} 
        />

        {editingCustomer && (
          <CustomerEditDialog
            customer={editingCustomer}
            onClose={() => setEditingCustomer(null)}
            onUpdate={handleUpdateCustomer} 
            onDelete={handleDeleteCustomer} 
          />
        )}

        {selectedCustomer && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mt-6">
            <h2 className="text-lg font-semibold mb-6 text-slate-100">API Keys for {selectedCustomer.DisplayName}</h2>

            <ApiKeyList
              apiKeys={apiKeys}
              handleUpdateNickname={handleUpdateNickname}
              handleToggleKey={handleToggleKey}
              handleDeleteKeyConfirm={handleDeleteKey}
              setSuccess={setSuccess}
              setError={setError}
            />

            <GenerateKeyForm
              teamProducts={teamProducts}
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              plans={plans}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              loadingPlans={loadingPlans}
              fetchPlans={fetchPlans}
              handleGenerateKey={handleGenerateKey}
              isGenerating={isGenerating}
              keyForm={keyForm}
              setKeyForm={setKeyForm}
            />

            <CustomerSelfServicePanel
              customerGuid={selectedCustomer.CustomerGUID}
              customerName={selectedCustomer.DisplayName}
            />

          </div>
        )}
      </div>
    </div>
  );
}
